import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Issue, QueryParams, SuggestionStatus, YesOrNo } from '../types';
import {
  getIssues,
  getIssuesByImage,
  getIssuesByTemplateId,
} from '../utils/httpRequest';
import { RootState } from '../store';

export enum SearchType {
  SEARCH = 'SEARCH',
  IMAGE = 'IMAGE',
}
export interface IssueState {
  issues: Issue[];
  searchedIssues?: Issue[];
  searchType?: SearchType;
  searchTime?: number;
  loading: boolean;
  isSearchingByImage: boolean;
  error?: string;
}

const initialState: IssueState = {
  issues: [],
  isSearchingByImage: false,
  loading: false,
};

export const fetchIssues = createAsyncThunk(
  'issues/fetchIssues',
  async ({
    templateID,
    params,
  }: {
    templateID?: string;
    params?: QueryParams;
  }) => {
    const issues = await (templateID
      ? getIssuesByTemplateId(templateID, params)
      : getIssues(params));

    return issues;
  }
);

export const searchIssues = createAsyncThunk(
  'issues/searchIssues',
  async ({ params }: { params?: QueryParams }) => {
    const issues = await getIssues(params);

    return issues;
  }
);

export const searchIssuesByImage = createAsyncThunk(
  'issues/searchIssuesByImage',
  async ({ templateId, fileId }: { templateId: string; fileId: string }) => {
    const issues = await getIssuesByImage({ templateId, fileId });

    return issues;
  }
);

export const issuesSlice = createSlice({
  name: 'issues',
  initialState,
  reducers: {
    setIssues: (state, action: PayloadAction<Issue[]>) => {
      state.issues = action.payload;
    },
    addIssue: (state, action: PayloadAction<Issue>) => {
      state.issues = [action.payload, ...state.issues];
    },
    removeIssue: (state, action: PayloadAction<string>) => {
      state.issues = state.issues?.filter(
        ({ issueID }) => issueID !== action.payload
      );
    },
    updateIssue: (state, action: PayloadAction<Issue>) => {
      const index = state.issues.findIndex(
        ({ issueID }) => issueID === action.payload.issueID
      );
      const nextIssues = [...state.issues];
      nextIssues[index] = action.payload;
      state.issues = [...nextIssues];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIssues.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchIssues.fulfilled, (state, action) => {
        state.issues = action.payload.data || [];
        state.searchedIssues = action.payload.data || [];
        state.searchType = SearchType.SEARCH;
        state.loading = false;
      })
      .addCase(fetchIssues.rejected, (state, action) => {
        state.error = 'Error';
        state.loading = false;
      });
    builder
      .addCase(searchIssues.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchIssues.fulfilled, (state, action) => {
        state.searchedIssues = action.payload.data || [];
        state.searchType = SearchType.SEARCH;
        state.searchTime = new Date().getTime();
        state.loading = false;
      })
      .addCase(searchIssues.rejected, (state, action) => {
        state.searchedIssues = [];
        state.error = 'Error';
        state.loading = false;
      });
    builder
      .addCase(searchIssuesByImage.pending, (state) => {
        state.isSearchingByImage = true;
      })
      .addCase(searchIssuesByImage.fulfilled, (state, action) => {
        state.searchType = SearchType.IMAGE;
        state.searchedIssues = action.payload.data || [];
        state.searchTime = new Date().getTime();
        state.isSearchingByImage = false;
      })
      .addCase(searchIssuesByImage.rejected, (state, action) => {
        state.searchedIssues = [];
        state.error = 'Error';
        state.isSearchingByImage = false;
      });
  },
});

export const { setIssues, addIssue, removeIssue, updateIssue } =
  issuesSlice.actions;

export default issuesSlice.reducer;

export const selectIssues = (state: RootState) => state.issues.issues || [];

export const selectIssuesSearchType = (state: RootState) =>
  state.issues.searchType || [];

export const selectIsFetchingIssues = (state: RootState) =>
  state.issues.loading;

export const selectIsSearchingIssuesByImage = (state: RootState) =>
  state.issues.isSearchingByImage;

export const selectCurrentIssues = (state: RootState) =>
  (state.issues.issues || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.NO ||
      i.suggestionStatus === SuggestionStatus.REVIEWED ||
      i.suggestionStatus === SuggestionStatus.APPROVED
  );

export const selectSearchedIssues = (state: RootState) =>
  (state.issues.searchedIssues || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.NO ||
      i.suggestionStatus === SuggestionStatus.REVIEWED ||
      i.suggestionStatus === SuggestionStatus.APPROVED
  );

export const selectSearchTime = (state: RootState) => state.issues.searchTime;
